/**
 * @file keyboard-handler.ts
 * @description Manages global keyboard shortcuts for tool switching, undo/redo, clipboard operations, and zoom control.
 */

import { Tool, g } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { undoImage, redoImage } from '../event-dispatcher';
import { selectTool } from '../event-dispatcher';
import {
    deselect,
    invertSelection,
    selectAll,
    copySelection,
    pasteSelection,
    cutSelection
} from '@hcie/tools';
import { renderLayers } from '../render-engine';

export class KeyboardHandler {
    /**
     * @function handleKeyDown
     * @description Entry point for keyboard event routing.
     * @param {KeyboardEvent} event - The native keyboard event.
     * @returns {void}
     */
    public static handleKeyDown(event: KeyboardEvent): void {
        // [CRITICAL_LOGIC_START]
        // Guard: Prevent shortcuts when interaction is focused on form elements.
        const targetElement = event.target as HTMLElement;
        const isFocusingInput: boolean = ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetElement.tagName);
        if (isFocusingInput) {
            return;
        }

        const isControlPressed: boolean = event.ctrlKey || event.metaKey;
        const isShiftPressed: boolean = event.shiftKey;
        const keyPressed: string = event.key.toLowerCase();

        // 1. History Management (Undo / Redo)
        if (isControlPressed && keyPressed === 'z') {
            event.preventDefault();
            if (isShiftPressed) {
                redoImage();
            } else {
                undoImage();
            }
            return;
        }
        if (isControlPressed && keyPressed === 'y') {
            event.preventDefault();
            redoImage();
            return;
        }

        // 2. Selection Shortcuts
        if (isControlPressed) {
            switch (keyPressed) {
                case 'a':
                    event.preventDefault();
                    selectAll();
                    return;
                case 'd':
                    event.preventDefault();
                    deselect();
                    return;
                case 'i':
                    if (isShiftPressed) {
                        event.preventDefault();
                        invertSelection();
                    }
                    return;
            }
        }

        // 3. Clipboard Management
        if (isControlPressed) {
            switch (keyPressed) {
                case 'c':
                    event.preventDefault();
                    copySelection();
                    return;
                case 'v':
                    event.preventDefault();
                    pasteSelection();
                    return;
                case 'x':
                    event.preventDefault();
                    cutSelection();
                    return;
            }
        }

        // 4. Tool Hotkeys (Single character keys)
        if (!isControlPressed) {
            this.handleToolSwitchShortcuts(keyPressed);
        }

        // 5. Canvas View Control (Zoom)
        if (isControlPressed) {
            this.handleZoomShortcuts(event, keyPressed);
        }

        // 6. Interaction Lifecycle (Commit / Cancel)
        this.handleInteractionLifecycle(event);
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function handleToolSwitchShortcuts
     * @description Maps specific keys to application tools.
     * @private
     */
    private static handleToolSwitchShortcuts(key: string): void {
        // V toggles Viewer mode (not a tool switch)
        if (key === 'v') {
            EventBus.emit('TOGGLE_VIEWER_MODE');
            return;
        }

        const toolMap: Record<string, Tool> = {
            'b': Tool.Brush,
            'p': Tool.Pen,
            'e': Tool.Eraser,
            'g': Tool.Flood_Fill,
            't': Tool.Text,
            'm': Tool.MoveSelection,
            'q': Tool.MoveContent,   // was 'v' — V is now viewer toggle
            'l': Tool.Lasso,
            'r': Tool.RectSelect,
            'o': Tool.EllipseSelect,
            'c': Tool.CircleSelect
        };

        const selectedTool: Tool | undefined = toolMap[key];
        if (selectedTool) {
            selectTool(selectedTool);
        }
    }

    /**
     * @function handleZoomShortcuts
     * @private
     */
    private static handleZoomShortcuts(event: KeyboardEvent, key: string): void {
        if (key === '=' || key === '+' || event.key === '+') {
            event.preventDefault();
            g.zoom *= g.zoomFactor;
            renderLayers();
        } else if (key === '-') {
            event.preventDefault();
            g.zoom /= g.zoomFactor;
            renderLayers();
        } else if (key === '0') {
            event.preventDefault();
            g.zoom = 1;
            renderLayers();
        }
    }

    /**
     * @function handleInteractionLifecycle
     * @private
     */
    private static handleInteractionLifecycle(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (typeof (window as any).commitDrawing === 'function') {
                (window as any).commitDrawing();
                event.preventDefault();
            }
        } else if (event.key === 'Escape') {
            if (typeof (window as any).cancelDrawing === 'function') {
                (window as any).cancelDrawing();
                event.preventDefault();
            }
        }
    }
}
